"use client";

import * as React from "react";
import { ArrowRight, Search, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ProductsSearch, useWPQueryForSearch, buildProductSearchUrl } from "@/components/shop/ProductsSearch";
import { EcommerceProductsProvider, useEcommerceProducts, ProductPrice } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
type Language = "en" | "zh" | "fr" | "es" | "de";
function useWvcLanguage(): Language {
  const [lang, setLang] = React.useState<Language>("en");
  React.useEffect(() => {
    try {
      const htmlLang = document.documentElement.lang?.toLowerCase() || "";
      if (htmlLang.startsWith("zh")) setLang("zh");else if (htmlLang.startsWith("fr")) setLang("fr");else if (htmlLang.startsWith("es")) setLang("es");else if (htmlLang.startsWith("de")) setLang("de");else setLang("en");
    } catch {
      setLang("en");
    }
  }, []);
  return lang;
}
const translations = {
  en: {
    all: "All items",
    new: "New arrivals",
    dresses: "Dresses",
    tops: "Tops & knitwear",
    tailored: "Tailored outerwear",
    sets: "Sets & separates",
    accessories: "Accessories",
    newBadge: "New",
    searching: "Searching curated pieces...",
    loadError: "Unable to load search results. Please try again.",
    noMatches: "No matching pieces found.",
    viewAllResults: "View all matching results",
    liveSearch: "Live collection search",
    searchPlaceholder: "Type dress, coat, silk top...",
    searchHint: "Type a keyword to explore matching pieces.",
    autumnWinter: "Autumn / Winter Lookbook & Store",
    resultsFor: "Search results for",
    curated: "Curated wardrobe essentials for",
    everySeason: "every season.",
    discover: "Discover tailored silhouettes, structured outerwear, and versatile layers designed for lasting wear.",
    search: "Search garments & accessories...",
    freeShipping: "Free shipping over $150",
    easyReturns: "Easy 30-day returns",
    browseCategory: "Browse by category",
    editorial: "Editorial Feature",
    minimalist: "Minimalist Tailoring Collection",
    craftsmanship: "Craftsmanship",
    woolSilk: "100% natural wool & silk blends"
  },
  zh: {
    all: "所有项目",
    new: "新品上市",
    dresses: "连衣裙",
    tops: "上衣和针织",
    tailored: "定制外套",
    sets: "套装和单品",
    accessories: "配件",
    newBadge: "新",
    searching: "搜索策划件...",
    loadError: "无法加载搜索结果。请重试。",
    noMatches: "未找到匹配的件。",
    viewAllResults: "查看所有匹配的结果",
    liveSearch: "实时系列搜索",
    searchPlaceholder: "输入连衣裙、外套、丝绸上衣...",
    searchHint: "输入关键词以探索匹配的件。",
    autumnWinter: "秋冬查看手册和商店",
    resultsFor: "搜索结果为",
    curated: "策划衣橱必需品为",
    everySeason: "每个季节。",
    discover: "发现为持久穿着而设计的定制剪裁、结构化外套和多功能层。",
    search: "搜索服装和配件...",
    freeShipping: "超过$150免运费",
    easyReturns: "轻松30天退货",
    browseCategory: "按类别浏览",
    editorial: "编辑特色",
    minimalist: "极简主义裁剪系列",
    craftsmanship: "工艺",
    woolSilk: "100%天然羊毛和丝绸混纺"
  },
  fr: {
    all: "Tous les articles",
    new: "Nouveautés",
    dresses: "Robes",
    tops: "Hauts et Tricots",
    tailored: "Vêtements d'extérieur Taillés",
    sets: "Ensembles et Séparés",
    accessories: "Accessoires",
    newBadge: "Nouveau",
    searching: "Recherche de pièces curées...",
    loadError: "Impossible de charger les résultats de recherche. Veuillez réessayer.",
    noMatches: "Aucune pièce correspondante trouvée.",
    viewAllResults: "Afficher tous les résultats correspondants",
    liveSearch: "Recherche de Collection en Direct",
    searchPlaceholder: "Tapez robe, manteau, haut en soie...",
    searchHint: "Tapez un mot-clé pour explorer les pièces correspondantes.",
    autumnWinter: "Lookbook & Magasin Automne / Hiver",
    resultsFor: "Résultats de recherche pour",
    curated: "Essentiels de garde-robe curés pour",
    everySeason: "chaque saison.",
    discover: "Découvrez les silhouettes taillées, les vêtements d'extérieur structurés et les couches polyvalentes conçues pour une usure durable.",
    search: "Rechercher des vêtements et des accessoires...",
    freeShipping: "Livraison gratuite à partir de 150$",
    easyReturns: "Retours faciles de 30 jours",
    browseCategory: "Parcourir par Catégorie",
    editorial: "Caractéristique Éditoriale",
    minimalist: "Collection de Tailoring Minimaliste",
    craftsmanship: "Artisanat",
    woolSilk: "Mélanges 100% laine naturelle et soie"
  },
  es: {
    all: "Todos los artículos",
    new: "Nuevas Llegadas",
    dresses: "Vestidos",
    tops: "Tops y Punto",
    tailored: "Ropa de Abrigo Personalizada",
    sets: "Conjuntos y Separados",
    accessories: "Accesorios",
    newBadge: "Nuevo",
    searching: "Buscando piezas curadas...",
    loadError: "No se pueden cargar los resultados de búsqueda. Por favor inténtelo de nuevo.",
    noMatches: "No se encontraron piezas coincidentes.",
    viewAllResults: "Ver todos los resultados coincidentes",
    liveSearch: "Búsqueda de Colección en Vivo",
    searchPlaceholder: "Escribe vestido, abrigo, top de seda...",
    searchHint: "Escribe una palabra clave para explorar piezas coincidentes.",
    autumnWinter: "Lookbook y Tienda Otoño / Invierno",
    resultsFor: "Resultados de búsqueda para",
    curated: "Artículos esenciales del guardarropa curados para",
    everySeason: "cada temporada.",
    discover: "Descubre siluetas personalizadas, prendas de abrigo estructuradas y capas versátiles diseñadas para un desgaste duradero.",
    search: "Buscar prendas y accesorios...",
    freeShipping: "Envío gratuito a partir de $150",
    easyReturns: "Fácil devolución de 30 días",
    browseCategory: "Navegar por Categoría",
    editorial: "Característica Editorial",
    minimalist: "Colección de Sastrería Minimalista",
    craftsmanship: "Artesanía",
    woolSilk: "Mezclas de lana 100% natural y seda"
  },
  de: {
    all: "Alle Artikel",
    new: "Neue Ankünfte",
    dresses: "Kleider",
    tops: "Tops und Strickwaren",
    tailored: "Maßgeschneiderte Oberbekleidung",
    sets: "Sets und Separates",
    accessories: "Zubehör",
    newBadge: "Neu",
    searching: "Kuratierte Stücke werden gesucht...",
    loadError: "Suchergebnisse können nicht geladen werden. Bitte versuchen Sie es erneut.",
    noMatches: "Keine übereinstimmenden Teile gefunden.",
    viewAllResults: "Alle Übereinstimmungsergebnisse anzeigen",
    liveSearch: "Live-Sammlungssuche",
    searchPlaceholder: "Geben Sie Kleid, Mantel, Seidentop ein...",
    searchHint: "Geben Sie ein Schlüsselwort ein, um übereinstimmende Teile zu erkunden.",
    autumnWinter: "Herbst- / Winter-Lookbook und Speicher",
    resultsFor: "Suchergebnisse für",
    curated: "Kuratierte Essentials für",
    everySeason: "jede Jahreszeit.",
    discover: "Entdecken Sie maßgeschneiderte Silhouetten, strukturierte Oberbekleidung und vielseitige Schichten für langlebigen Verschleiß.",
    search: "Kleidung und Zubehör durchsuchen...",
    freeShipping: "Versand ab $150 kostenlos",
    easyReturns: "Einfache 30-Tage-Rückgaben",
    browseCategory: "Nach Kategorie durchsuchen",
    editorial: "Redaktionelle Funktion",
    minimalist: "Minimalistische Schneiderei-Kollektion",
    craftsmanship: "Handwerk",
    woolSilk: "100% natürliche Wolle und Seidenmischungen"
  }
};
interface CategoryPill {
  name: string;
  to: string;
  badge?: string;
}
function SearchResultsConsumer({
  t
}: {
  t: (typeof translations)["en"];
}) {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
        {t.searching}
      </div>;
  }
  if (error) {
    return <div className="py-4 text-center text-xs text-destructive">
        {t.loadError}
      </div>;
  }
  if (!products || products.length === 0) {
    return <div className="py-6 text-center text-sm text-muted-foreground">
        {t.noMatches}
      </div>;
  }
  const visibleProducts = products.slice(0, 6);
  return <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {visibleProducts.map((product, i) => <Link key={product.id} data-index={i} to={product.link || "/ShopPage"} className="group flex items-center gap-3 border border-border bg-card p-2 text-left transition-colors hover:bg-muted">
          {product.featured_image_url ? <div className="size-12 shrink-0 overflow-hidden bg-muted">
              <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" decoding="async" />
            </div> : <div className="flex size-12 shrink-0 items-center justify-center bg-muted text-[10px] text-muted-foreground">
              No image
            </div>}
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium text-foreground">
              {product.title}
            </p>
            <div className="mt-0.5 text-xs text-primary">
              <ProductPrice product={product} />
            </div>
          </div>
        </Link>)}
    </div>;
}
function ShopSearchPopover({
  t
}: {
  t: (typeof translations)["en"];
}) {
  const [searchValue, setSearchValue] = React.useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  return <EcommerceProductsProvider wp_query={wp_query}>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-start border-border bg-background px-4 py-2.5 text-sm text-muted-foreground hover:border-foreground hover:bg-card hover:text-foreground sm:w-72">
            <Search className="mr-2 size-4 text-foreground" />
            <span>{t.search}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[calc(100vw-3rem)] max-w-lg min-w-[320px] space-y-3 rounded-none border border-border bg-background p-4 shadow-lg sm:w-[480px]">
          <div className="space-y-1">
            <label htmlFor="shop-product-search" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {t.liveSearch}
            </label>
            <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder={t.searchPlaceholder} className="w-full" />
          </div>

          <div className="max-h-72 overflow-y-auto pt-1">
            {!hasSearchQuery ? <p className="py-4 text-center text-xs text-muted-foreground">
                {t.searchHint}
              </p> : <div className="space-y-3">
                <SearchResultsConsumer t={t} />
                <div className="border-t border-border pt-2 text-right">
                  <Link to={buildProductSearchUrl(searchValue)} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                    <span>{t.viewAllResults}</span>
                    <ArrowRight className="size-3" />
                  </Link>
                </div>
              </div>}
          </div>
        </PopoverContent>
      </Popover>
    </EcommerceProductsProvider>;
}
export default function ShopHero() {
  const language = useWvcLanguage();
  const t = translations[language];
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const categoryPills: CategoryPill[] = [{
    name: t.all,
    to: "/ShopPage"
  }, {
    name: t.new,
    to: "/ShopPage",
    badge: t.newBadge
  }, {
    name: t.dresses,
    to: "/ShopPage"
  }, {
    name: t.tops,
    to: "/ShopPage"
  }, {
    name: t.tailored,
    to: "/ShopPage"
  }, {
    name: t.sets,
    to: "/ShopPage"
  }, {
    name: t.accessories,
    to: "/ShopPage"
  }];
  React.useEffect(() => {
    try {
      const params = typeof window !== "undefined" && typeof window.wvcClient !== "undefined" && typeof window.wvcClient.getCurrentWPQueryParams === "function" ? window.wvcClient.getCurrentWPQueryParams() : {};
      const term = String(params?.s ?? params?.search ?? "").trim();
      setSearchTerm(term);
    } catch {
      setSearchTerm("");
    }
  }, []);
  return <section id="shop-hero" data-nav="light" className="relative overflow-hidden border-b border-border bg-background text-foreground">
      <div className="mx-auto max-w-[1360px] px-6 py-12 md:px-10 md:py-16 lg:px-16 lg:py-20">
        <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="flex flex-col space-y-6 lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              <Sparkles className="size-3 text-primary" />
              <span>{t.autumnWinter}</span>
            </div>

            <div className="space-y-3">
              <h1 className="font-serif text-4xl font-normal tracking-tight text-foreground sm:text-5xl lg:text-6xl">
                {searchTerm ? <>
                    {t.resultsFor}{" "}
                    <span className="italic text-primary">
                      &ldquo;{searchTerm}&rdquo;
                    </span>
                  </> : <>
                    {t.curated}{" "}
                    <span className="italic text-primary">{t.everySeason}</span>
                  </>}
              </h1>
              <p className="max-w-xl text-base text-muted-foreground sm:text-lg font-light leading-relaxed">
                {t.discover}
              </p>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <ShopSearchPopover t={t} />
              <div className="text-xs text-muted-foreground">
                <span>{t.freeShipping} &bull; {t.easyReturns}</span>
              </div>
            </div>

            <div className="pt-2">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t.browseCategory}
              </p>
              <nav aria-label="Shop categories" className="flex flex-wrap gap-2">
                {categoryPills.map((pill, i) => <Link key={pill.name} data-index={i} to={pill.to} className="inline-flex items-center gap-1.5 border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-foreground hover:bg-muted">
                    <span>{pill.name}</span>
                    {pill.badge && <span className="bg-primary px-1.5 py-0.2 text-[9px] font-semibold text-primary-foreground">
                        {pill.badge}
                      </span>}
                  </Link>)}
              </nav>
            </div>
          </div>

          <div className="relative lg:col-span-5">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              <div className="relative aspect-[3/4] overflow-hidden border border-border bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/shop_hero_lookbook_model.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/shop_hero_lookbook_model.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/shop_hero_lookbook_model.png-wvc-sizes" alt="Model showcasing high-end minimalist women outerwear and tailored dress" className="h-full w-full object-cover object-top" loading="lazy" decoding="async" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-6 text-white">
                  <p className="text-xs uppercase tracking-widest text-primary-foreground/90 font-medium">
                    {t.editorial}
                  </p>
                  <p className="font-serif text-lg font-normal text-white">
                    {t.minimalist}
                  </p>
                </div>
              </div>

              <div className="absolute -bottom-4 -left-4 hidden border border-border bg-card p-4 shadow-md sm:block">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t.craftsmanship}
                </p>
                <p className="text-sm font-medium text-foreground">
                  {t.woolSilk}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}